import { containsCapabilityUrl, redactTokenUrl } from './capabilityPrivacy'
import { deriveUtmsFromHsa } from './utms'

// eslint-disable-next-line no-control-regex
const isoSafe = /[^\u0000-\u00ff]/g

/** Capability URLs are dropped; redacted-route tokens become `[token]` (the
 *  landing page still attributes, the credential does not travel \u2014 it would
 *  otherwise reach HubSpot via the server's first-touch properties). */
const safeAttributionValue = (value: string | undefined): string | undefined =>
  !value || containsCapabilityUrl(value) ? undefined : redactTokenUrl(value)

const utmCookieParams = [
  'utm_page',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_referrer',
  'utm_landing_timestamp',
  'gclid',
  'fbclid',
  'msclkid',
  // Raw HubSpot Ads metadata that doesn't project cleanly into utm_*:
  //  - hsa_mt = bid match type (exact/phrase/broad)
  //  - hsa_grp = ad group id (utm_campaign already holds the campaign)
  //  - hsa_net = ad network short name (adwords/facebook/bing/linkedin)
  //  - hsa_ad = ad / creative id (utm_content already mirrors this)
  // Underscore → dash header conversion below produces hsa-mt etc.
  'hsa_mt',
  'hsa_grp',
  'hsa_net',
  'hsa_ad'
]

const isPrivateRequest = (url: string): boolean =>
  containsCapabilityUrl(url.split('?')[0])

/** Drop inherited attribution too: operation headers can outlive a navigation. */
export const sanitizeAttributionHeaders = (
  headers: Record<string, string> | undefined,
  url: string,
  privateDocument = false
): Record<string, string> => {
  const suppressed = privateDocument || isPrivateRequest(url)
  const attributionNames = new Set(
    utmCookieParams.map((key) => key.replace(/_/g, '-'))
  )
  return Object.fromEntries(
    Object.entries(headers ?? {}).filter(
      ([key, value]) =>
        !attributionNames.has(key.toLowerCase()) ||
        (!suppressed && !containsCapabilityUrl(value))
    )
  )
}

/** Preserve normal first-touch attribution; bearer capabilities are never metadata. */
export const getAttributionHeaders = ({
  cookies,
  url,
  referrer,
  privateDocument = false
}: {
  cookies: Record<string, string>
  url: string
  referrer?: string
  privateDocument?: boolean
}): Record<string, string> => {
  // Includes /_next/data/.../aliado/contactado/... SSR requests as well as pages.
  if (privateDocument || isPrivateRequest(url)) return {}

  // URL fallback when cookies don't carry UTMs yet.
  //
  // The race we're closing: on listing pages, Details/index.tsx fires a
  // re-fetch in useEffect to trigger the server-side VISIT event. That
  // useEffect runs BEFORE <UTMs />'s useEffect (deeper-in-tree effects
  // commit first), so on first landing the cookies are still empty —
  // Apollo sends the request without utm-* headers and the server
  // stores VISIT with no attribution. This dropped ~95% of paid-traffic
  // VISITs into the "(direct)" bucket in the marketing report.
  //
  // Read directly from the URL/referer/path as a fallback. Cookies still
  // win when present (returning users keep their captured first-touch).
  // Applies on both SSR (req.url) and client (window.location).
  const fallback: Record<string, string> = {}
  const fillFromSearch = (search: URLSearchParams) => {
    for (const param of [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
      'gclid',
      'fbclid',
      'msclkid'
    ]) {
      const v = search.get(param)
      if (v) fallback[param] = v
    }
    // Derive utm_* from hsa_* (HubSpot Ads auto-tagging) when explicit
    // utm_* params are absent. Most SpotMe campaigns rely on hsa_* and
    // gclid/fbclid auto-tagging rather than tagging URLs with utm_*
    // directly, so this is the dominant attribution path on first touch.
    // Explicit utm_* (already filled above) wins.
    const hsaQuery: Record<string, string> = {}
    for (const [k, v] of search.entries()) {
      if (k.startsWith('hsa_')) hsaQuery[k] = v
    }
    if (Object.keys(hsaQuery).length > 0) {
      const derived = deriveUtmsFromHsa(hsaQuery)
      for (const [k, v] of Object.entries(derived)) {
        if (typeof v === 'string' && v && !fallback[k]) fallback[k] = v
      }
      // Also keep the raw hsa_* values that don't project into utm_* —
      // match type and ad group id are the ones we want for slicing.
      for (const key of ['hsa_mt', 'hsa_grp', 'hsa_net', 'hsa_ad']) {
        if (hsaQuery[key] && !fallback[key]) fallback[key] = hsaQuery[key]
      }
    }
  }
  const queryStart = url.indexOf('?')
  if (queryStart >= 0) {
    fillFromSearch(new URLSearchParams(url.slice(queryStart + 1)))
  }
  const path = queryStart >= 0 ? url.slice(0, queryStart) : url
  if (path && !fallback.utm_page) fallback.utm_page = path
  if (referrer && !fallback.utm_referrer) fallback.utm_referrer = referrer

  return utmCookieParams.reduce(
    (acc, param) => {
      const cookieVal =
        typeof cookies[param] === 'string' ? cookies[param] : undefined
      const fallbackVal = fallback[param]
      let value =
        safeAttributionValue(cookieVal) ?? safeAttributionValue(fallbackVal)
      if (!value) return acc
      if (isoSafe.test(value)) value = value.replace(isoSafe, '')
      return { ...acc, [param.replace(/_/g, '-')]: value }
    },
    {} as Record<string, string>
  )
}
